import { ArrowsLeftRightIcon } from "@phosphor-icons/react/dist/csr/ArrowsLeftRight";
import { ArrowUDownLeftIcon } from "@phosphor-icons/react/dist/csr/ArrowUDownLeft";
import { UserSwitchIcon } from "@phosphor-icons/react/dist/csr/UserSwitch";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { useImpersonation } from "@/core/impersonation";
import { ImpersonationIdentity } from "./impersonation-identity";

export const DevBarImpersonation = ({
  onImpersonate,
}: {
  onImpersonate?: () => void;
}) => {
  const { isImpersonating, impersonatedUser, endImpersonation, isSavePending } =
    useImpersonation();
  const navigate = useNavigate();
  const [isStopping, setIsStopping] = useState(false);
  const stop = async () => {
    setIsStopping(true);
    try {
      await endImpersonation();
      navigate("/");
    } catch (error) {
      toast.error("Couldn't stop impersonating. Try again.", {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsStopping(false);
    }
  };
  if (!isImpersonating) {
    if (!onImpersonate) return null;
    return (
      <Button
        variant="ghost"
        size="xs"
        className="mx-2 max-md:size-11"
        aria-label="Impersonate user"
        onClick={onImpersonate}
      >
        <UserSwitchIcon aria-hidden />
        <span className="max-md:hidden">Impersonate user</span>
      </Button>
    );
  }
  return (
    <Stack
      direction="row"
      align="center"
      gap={3}
      role="group"
      aria-label="Impersonation"
      aria-busy={isSavePending || isStopping}
      title={
        isSavePending
          ? "Wait for the current save to finish before changing identity."
          : undefined
      }
      className="light min-w-0 self-stretch bg-warning-subtle px-3 text-ink max-md:h-14 max-md:w-full"
    >
      {impersonatedUser ? (
        <ImpersonationIdentity user={impersonatedUser} />
      ) : (
        <Text variant="caption">Loading acting identity…</Text>
      )}
      <Stack direction="row" align="center" gap={1} className="shrink-0">
        {onImpersonate && (
          <Button
            variant="secondary"
            size="xs"
            className="max-md:h-11 max-md:w-11"
            aria-label="Switch user"
            disabled={isSavePending || isStopping}
            onClick={onImpersonate}
          >
            <ArrowsLeftRightIcon aria-hidden />
            <span className="max-md:hidden">Switch</span>
          </Button>
        )}
        <Button
          size="xs"
          variant="secondary"
          className="max-md:h-11"
          aria-label="Stop impersonating"
          disabled={isSavePending}
          loading={isStopping}
          onClick={() => void stop()}
        >
          <ArrowUDownLeftIcon aria-hidden />
          Stop
        </Button>
      </Stack>
    </Stack>
  );
};
